<template>
    <div class="opportunities-view-wrap">
        <!-- ============ Erstes Oeffnen: Index wird aufgebaut ============ -->
        <v-card v-if="preparing" class="rounded_card quaroshadow opp-preparing mt-1" outlined>
            <div class="opp-preparing__icon"><v-icon color="primary">mdi-database-clock-outline</v-icon></div>
            <div class="opp-preparing__title">Preparing the data for this date range</div>
            <div class="opp-preparing__text">{{ progress.done }} of {{ progress.total }} import days indexed. Each day is prepared only once; after that the tab loads in about a second.</div>
            <v-progress-linear :value="preparingPercent" height="6" rounded color="primary" background-color="grey lighten-3" class="opp-preparing__bar"></v-progress-linear>
        </v-card>

        <template v-else>
            <!-- ============ Kennzahlen ============ -->
            <v-row class="mt-1">
                <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="12" sm="6" md="3" class="d-flex">
                    <v-card class="rounded_card quaroshadow pa-4 opp-kpi flex-grow-1" :class="`opp-kpi--${kpi.tone}`">
                        <div class="opp-kpi__head">
                            <span class="opp-kpi__icon"><v-icon small :color="kpi.iconColor">{{ kpi.icon }}</v-icon></span>
                            <span class="opp-kpi__label">
                                {{ kpi.label }}
                                <v-tooltip top max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span>{{ kpi.tooltip }}</span>
                                </v-tooltip>
                            </span>
                        </div>
                        <div class="opp-kpi__value">
                            {{ kpi.value }}
                            <span v-if="kpi.total" class="opp-kpi__total">of {{ kpi.total }}</span>
                            <v-tooltip top v-if="kpi.delta" max-width="260">
                                <template v-slot:activator="{ on, attrs }">
                                    <span class="opp-delta" :class="kpi.delta.good ? 'opp-delta--good' : 'opp-delta--bad'" v-bind="attrs" v-on="on">
                                        <v-icon x-small>{{ kpi.delta.up ? "mdi-arrow-up" : "mdi-arrow-down" }}</v-icon>{{ kpi.delta.text }}
                                    </span>
                                </template>
                                <span>Compared with the previous period of the same length ({{ previousRangeLabel }}).</span>
                            </v-tooltip>
                        </div>
                        <div class="opp-kpi__bar-row">
                            <template v-if="kpi.share != null">
                                <v-progress-linear :value="kpi.share" :color="kpi.barColor" background-color="grey lighten-3" rounded height="4" class="opp-kpi__bar" />
                                <span class="opp-kpi__share" :class="`opp-kpi__share--${kpi.tone}`">{{ kpi.share }}%</span>
                            </template>
                            <span v-else class="opp-kpi__hint">{{ kpi.hint }}</span>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- ============ Hauptkarte ============ -->
            <v-card class="rounded_card quaroshadow opp-card mt-5">
                <v-toolbar flat class="gradient-toolbar elevation-0">
                    <div class="d-flex align-center">
                        <h2 class="project-data-heading__title">Opportunities</h2>
                        <v-tooltip top max-width="440">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span
                                >Every prompt is asked on several AI platforms and on several days. Each answer that does <b>not</b> mention {{ ownBrandLabel }} is a <b>gap</b>. A gap is <b>major</b> when you are never named, dropped
                                out of the latest answers, or are missing from at least half of the answers. Minor gaps are usually normal fluctuation and stay hidden unless you switch them on.</span
                            >
                        </v-tooltip>
                    </div>
                    <v-spacer />
                    <v-switch v-model="includeWeak" dense hide-details inset class="opp-weak-switch mr-3" :label="weakSwitchLabel"></v-switch>
                    <v-btn rounded color="primary" :disabled="!rows.length || exporting" :loading="exporting" @click="exportRows" aria-label="Export opportunities">
                        <v-icon>mdi-cloud-download</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text class="opp-card__body">
                    <!-- Kontextzeile mit Erklaerung, was eine Luecke ist. Einklappbar; der Zustand wird pro Browser gemerkt. -->
                    <div class="opp-explainer">
                        <div class="opp-explainer__line">
                            <span class="opp-explainer__hint">AI answers that do not mention <b>{{ ownBrandLabel }}</b>. {{ includeWeak ? "Major and minor gaps." : "Major gaps only." }}</span>
                            <button type="button" class="opp-explainer__toggle" :aria-expanded="explainerOpen ? 'true' : 'false'" @click="toggleExplainer">
                                <v-icon small>mdi-lightbulb-outline</v-icon>
                                <span class="opp-explainer__title">What counts as a gap?</span>
                                <v-icon small class="opp-explainer__chevron">{{ explainerOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                            </button>
                        </div>
                        <div v-if="explainerOpen" class="opp-explainer__body">
                            <p class="opp-explainer__text">
                                Every prompt is asked on several AI platforms and on several days, so one prompt produces many answers. Each answer that does <b>not</b> mention {{ ownBrandLabel }} is a <b>gap</b>. A prompt with many
                                gaps is an opportunity: that is where the models recommend someone else instead of you.
                            </p>
                            <div class="opp-explainer__types">
                                <div v-for="type in gapTypeOrder" :key="`ex-${type}`" class="opp-explainer__type">
                                    <span class="opp-pill" :class="`opp-pill--${type}`">{{ gapTypeLabel(type) }}</span>
                                    <span class="opp-explainer__type-text">{{ gapTypeExplain(type) }}</span>
                                </div>
                            </div>
                            <p class="opp-explainer__text opp-explainer__text--muted">
                                By default you only see <b>major gaps</b>: never named, dropped out, or missing from at least half of the answers. <b>Minor gaps</b> (missing from fewer answers and still named in the latest ones) are
                                usually normal fluctuation. The switch at the top right adds them.
                            </p>
                        </div>
                    </div>

                    <!-- Kontextzeile: Wettbewerber-Set + aktive Filter -->
                    <div class="opp-context">
                        <div class="opp-context__group" v-if="competitors.length">
                            <span class="opp-context__label">{{ competitorSourceLabel }}</span>
                            <v-chip
                                v-for="c in competitors"
                                :key="`comp-${c.canonicalName}`"
                                small
                                class="opp-chip"
                                :color="competitorFilter === c.canonicalName ? 'primary' : 'teal lighten-5'"
                                :text-color="competitorFilter === c.canonicalName ? 'white' : 'teal darken-2'"
                                @click="toggleCompetitor(c.canonicalName)">
                                {{ c.canonicalName }}
                                <v-icon v-if="competitorFilter === c.canonicalName" x-small right>mdi-close</v-icon>
                            </v-chip>
                        </div>
                        <div class="opp-context__group" v-if="topicFilter">
                            <span class="opp-context__label">Topic</span>
                            <v-chip small color="primary" text-color="white" class="opp-chip" close @click:close="topicFilter = null">{{ topicFilter }}</v-chip>
                        </div>
                        <v-spacer />
                        <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="primary" class="mr-1"></v-progress-circular>
                    </div>

                    <!-- Ansichten; rechts daneben der Karten/Plattform-Umschalter der Topics-Ansicht -->
                    <div class="opp-seg-row">
                        <div class="opp-seg" role="tablist">
                            <button v-for="tab in viewTabs" :key="tab.key" type="button" role="tab" class="opp-seg__btn" :class="{ 'opp-seg__btn--active': view === tab.key }" :aria-selected="view === tab.key" @click="view = tab.key">
                                <v-icon x-small class="opp-seg__icon">{{ tab.icon }}</v-icon>
                                {{ tab.label }}
                                <span v-if="tab.count != null" class="opp-seg__count">{{ formatNum(tab.count) }}</span>
                            </button>
                        </div>
                        <div v-if="view === 'topics'" class="opp-seg opp-seg--small" role="group" aria-label="Topic layout">
                            <button type="button" class="opp-seg__btn" :class="{ 'opp-seg__btn--active': topicMode === 'cards' }" @click="topicMode = 'cards'"><v-icon x-small class="opp-seg__icon">mdi-view-grid-outline</v-icon>Cards</button>
                            <button type="button" class="opp-seg__btn" :class="{ 'opp-seg__btn--active': topicMode === 'heatmap' }" @click="topicMode = 'heatmap'"><v-icon x-small class="opp-seg__icon">mdi-grid</v-icon>By platform</button>
                        </div>
                    </div>

                    <!-- ======== TOPICS ======== -->
                    <div v-if="view === 'topics'" class="opp-section">
                        <div class="opp-section__subtitle">Topics with the most gaps first. Presence = share of the topic's answers that mention {{ ownBrandLabel }}. Click a topic to see its prompts.</div>
                        <div v-if="topicMode === 'heatmap' && topicPlatform.rows.length" class="opp-heat-wrap">
                            <table class="opp-heat">
                                <thead>
                                    <tr>
                                        <th class="opp-heat__topic">Topic</th>
                                        <th>All</th>
                                        <th v-for="pf in topicPlatform.platforms" :key="`hp-${pf}`">{{ platformLabel(pf) }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in topicPlatform.rows" :key="`hr-${row.tag}`" @click="openTopic(row.tag)">
                                        <td class="opp-heat__topic">
                                            {{ row.tag }}
                                            <span class="opp-heat__n">{{ formatNum(row.responses) }} answers</span>
                                        </td>
                                        <td><span class="opp-heat__cell" :style="heatStyle(row.presenceShare)">{{ Math.round(row.presenceShare * 100) }}%</span></td>
                                        <td v-for="cell in row.cells" :key="`hc-${row.tag}-${cell.platform}`">
                                            <span v-if="cell.presenceShare === null" class="opp-heat__cell opp-heat__cell--empty" title="No answers on this platform">–</span>
                                            <span v-else class="opp-heat__cell" :style="heatStyle(cell.presenceShare)" :title="`${formatNum(cell.responses)} answers`">{{ Math.round(cell.presenceShare * 100) }}%</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="opp-heat__legend">
                                <span class="opp-heat__cell" :style="heatStyle(0)">0%</span>
                                <span class="opp-heat__cell" :style="heatStyle(0.25)">25%</span>
                                <span class="opp-heat__cell" :style="heatStyle(0.5)">50%</span>
                                <span class="opp-heat__cell" :style="heatStyle(0.75)">75%</span>
                                <span class="opp-heat__cell" :style="heatStyle(1)">100%</span>
                                <span class="opp-muted ml-2">share of answers that mention {{ ownBrandLabel }}</span>
                            </div>
                        </div>
                        <div v-if="!topics.length && !loading" class="opp-empty">
                            <v-icon color="grey lighten-1">mdi-tag-off-outline</v-icon>
                            <div>No topic tags on these prompts. Tag your prompts to get a topic overview, or switch to the Prompts view.</div>
                        </div>
                        <div class="opp-topic-grid" v-if="topicMode === 'cards'">
                            <div v-for="t in topics" :key="`topic-${t.tag}`" class="opp-topic" role="button" tabindex="0" @click="openTopic(t.tag)" @keydown.enter="openTopic(t.tag)">
                                <div class="opp-topic__head">
                                    <div class="opp-topic__title">{{ t.tag }}</div>
                                    <div class="opp-topic__score" :title="'Average score (0–100) of the gap prompts in this topic. Higher = more worth working on.'">
                                        <span class="opp-topic__score-label">Score</span>
                                        <span class="opp-topic__score-value">{{ t.score }}</span>
                                    </div>
                                </div>
                                <div class="opp-topic__presence">
                                    <div class="opp-topic__presence-row">
                                        <span class="opp-topic__presence-pct">{{ Math.round(t.presenceShare * 100) }}%</span>
                                        <span class="opp-topic__presence-text">presence in {{ formatNum(t.responses) }} answers</span>
                                        <span v-if="t.presenceDelta !== null && t.presenceDelta !== undefined && Math.round(t.presenceDelta * 100) !== 0" class="opp-delta" :class="t.presenceDelta > 0 ? 'opp-delta--good' : 'opp-delta--bad'" title="Change in presence versus the previous period">
                                            <v-icon x-small>{{ t.presenceDelta > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}</v-icon>{{ Math.abs(Math.round(t.presenceDelta * 100)) }}pp
                                        </span>
                                    </div>
                                    <v-progress-linear :value="Math.round(t.presenceShare * 100)" color="primary" background-color="grey lighten-3" rounded height="6" />
                                </div>
                                <div class="opp-topic__gaps">
                                    <span class="opp-topic__gaps-count"><b>{{ t.gapPrompts }}</b> of {{ t.prompts }} prompts with a gap</span>
                                    <span class="opp-topic__pills">
                                        <span v-for="pill in topicPills(t)" :key="`tt-${t.tag}-${pill.type}`" class="opp-pill" :class="`opp-pill--${pill.type}`" :title="gapTypeExplain(pill.type)">{{ pill.count }} {{ gapTypeShort(pill.type) }}</span>
                                    </span>
                                </div>
                                <div class="opp-topic__foot" v-if="t.topCompetitors.length || t.topDomains.length">
                                    <div class="opp-topic__row" v-if="t.topCompetitors.length">
                                        <span class="opp-topic__row-label">Named instead</span>
                                        <span class="opp-topic__row-value">
                                            <span v-for="c in t.topCompetitors" :key="`tc-${t.tag}-${c.name}`" class="opp-tag opp-tag--teal">{{ c.name }}</span>
                                        </span>
                                    </div>
                                    <div class="opp-topic__row" v-if="t.topDomains.length">
                                        <span class="opp-topic__row-label">Cited there</span>
                                        <span class="opp-topic__row-value">
                                            <span v-for="d in t.topDomains.slice(0, 3)" :key="`td-${t.tag}-${d.name}`" class="opp-tag">{{ d.name }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ======== CHANGES ======== -->
                    <div v-if="view === 'changes'" class="opp-section">
                        <div v-if="!trend.hasPrevious" class="opp-empty">
                            <v-icon color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
                            <div>No data in the previous period, so there is nothing to compare against yet.</div>
                        </div>
                        <template v-else>
                            <div class="opp-section__subtitle">Compared with the previous period of the same length ({{ previousRangeLabel }}), using the same competitor set.</div>
                            <div class="opp-change-grid">
                                <div class="opp-change opp-change--bad" role="button" tabindex="0" @click="showNewGaps" @keydown.enter="showNewGaps">
                                    <div class="opp-change__value">{{ formatNum(trend.newGaps) }}</div>
                                    <div class="opp-change__label">New major gaps</div>
                                    <div class="opp-change__hint">no major gap in the previous period, major gap now · click to list</div>
                                </div>
                                <div class="opp-change opp-change--good">
                                    <div class="opp-change__value">{{ formatNum(trend.resolved) }}</div>
                                    <div class="opp-change__label">No longer a major gap</div>
                                    <div class="opp-change__hint">major gap in the previous period, better now · listed below</div>
                                </div>
                                <div class="opp-change">
                                    <div class="opp-change__value">{{ formatNum(trend.persisting) }}</div>
                                    <div class="opp-change__label">Still open</div>
                                    <div class="opp-change__hint">major gap in both periods</div>
                                </div>
                            </div>
                            <div class="opp-expand__label mt-5 mb-2">Recovered prompts</div>
                            <div v-if="!resolvedRows.length" class="opp-muted">None in this range.</div>
                            <div v-else class="opp-query-list">
                                <div v-for="r in resolvedRows" :key="`res-${r.prompt}`" class="opp-resolved">
                                    <span class="opp-resolved__prompt">{{ r.prompt }}</span>
                                    <span class="opp-resolved__shares">
                                        <span class="opp-resolved__from">{{ Math.round(r.prevGapShare * 100) }}%</span>
                                        <v-icon x-small color="grey">mdi-arrow-right</v-icon>
                                        <span class="opp-resolved__to">{{ Math.round(r.gapShare * 100) }}% of answers without you</span>
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- ======== PROMPTS ======== -->
                    <div v-if="view === 'prompts'" class="opp-section">
                        <div class="opp-filterbar">
                            <div class="opp-filterbar__chips">
                                <v-chip
                                    v-for="opt in gapTypeOptions"
                                    :key="`gt-${opt.value}`"
                                    small
                                    class="opp-chip"
                                    :color="gapTypeFilter === opt.value ? 'primary' : 'grey lighten-4'"
                                    :text-color="gapTypeFilter === opt.value ? 'white' : 'grey darken-3'"
                                    @click="gapTypeFilter = opt.value">
                                    {{ opt.text }}
                                    <span class="opp-chip__count" v-if="opt.count !== null">{{ opt.count }}</span>
                                </v-chip>
                            </div>
                            <v-text-field v-model="gapSearch" dense hide-details outlined clearable prepend-inner-icon="mdi-magnify" label="Search prompt" class="opp-search" @input="onGapSearchInput" />
                        </div>
                        <div class="opp-filterbar opp-filterbar--second">
                            <div class="opp-filterbar__chips">
                                <span class="opp-context__label">Worklist</span>
                                <v-chip
                                    v-for="opt in statusFilterOptions"
                                    :key="`sf-${opt.value}`"
                                    small
                                    class="opp-chip"
                                    :color="statusFilter === opt.value ? 'primary' : 'grey lighten-4'"
                                    :text-color="statusFilter === opt.value ? 'white' : 'grey darken-3'"
                                    @click="statusFilter = opt.value">
                                    {{ opt.text }}
                                    <span class="opp-chip__count" v-if="opt.count !== null">{{ opt.count }}</span>
                                </v-chip>
                            </div>
                            <v-chip v-if="trend.hasPrevious" small class="opp-chip" :color="trendFilter === 'new' ? 'primary' : 'grey lighten-4'" :text-color="trendFilter === 'new' ? 'white' : 'grey darken-3'" @click="trendFilter = trendFilter === 'new' ? null : 'new'">
                                <v-icon x-small left>mdi-new-box</v-icon>
                                New since previous period
                                <span class="opp-chip__count">{{ trend.newGaps }}</span>
                            </v-chip>
                        </div>

                        <v-data-table
                            :headers="gapHeaders"
                            :items="rows"
                            :server-items-length="total"
                            :options.sync="gapOptions"
                            :loading="loading"
                            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                            item-key="prompt"
                            show-expand
                            :expanded.sync="gapExpanded"
                            class="opp-table">
                            <template v-slot:item.prompt="{ item }">
                                <div class="opp-prompt">{{ item.prompt }}</div>
                                <div class="opp-prompt__tags" v-if="item.tags && item.tags.length">
                                    <span v-for="t in visibleTags(item.tags)" :key="`t-${item.prompt}-${t}`" class="opp-tag opp-tag--link" @click.stop="openTopic(t)">{{ t }}</span>
                                </div>
                            </template>
                            <template v-slot:item.gapType="{ item }">
                                <span v-if="item.gapType === 'covered'" class="opp-pill opp-pill--closed" :title="`${ownBrandLabel} is named in every answer again`">Gap closed</span>
                                <span v-else class="opp-pill" :class="`opp-pill--${item.gapType}`" :title="gapTypeExplain(item.gapType)">{{ gapTypeLabel(item.gapType) }}</span>
                                <span v-if="item.trend === 'new'" class="opp-pill opp-pill--new" title="Was not a major gap in the previous period">new</span>
                                <div v-if="item.signal === 'weak'" class="opp-weak" :title="minorGapExplain">minor gap</div>
                            </template>
                            <template v-slot:item.gapResponses="{ item }">
                                <div class="opp-num">{{ item.gapResponses }} <span class="opp-num__of">/ {{ item.responses }}</span></div>
                                <div class="opp-meter opp-meter--sm">
                                    <div class="opp-meter__fill opp-meter__fill--gap" :style="{ width: Math.round(item.gapShare * 100) + '%' }"></div>
                                </div>
                                <div class="opp-num__sub">{{ Math.round(item.gapShare * 100) }}% of answers without you</div>
                            </template>
                            <template v-slot:item.gapPlatforms="{ item }">
                                <div class="opp-tags">
                                    <span v-for="p in item.gapPlatforms" :key="`p-${item.prompt}-${p}`" class="opp-tag">{{ platformLabel(p) }}</span>
                                </div>
                            </template>
                            <template v-slot:item.competitors="{ item }">
                                <span v-if="!item.competitors.length" class="opp-muted">no competitor named</span>
                                <span v-else class="opp-tags">
                                    <span v-for="c in item.competitors.slice(0, 3)" :key="`c-${item.prompt}-${c.name}`" class="opp-tag opp-tag--teal opp-tag--link" @click.stop="toggleCompetitor(c.name)">{{ c.name }} <em>{{ c.count }}</em></span>
                                    <span v-if="item.competitors.length > 3" class="opp-muted">+{{ item.competitors.length - 3 }}</span>
                                </span>
                            </template>
                            <template v-slot:item.status="{ item }">
                                <v-menu offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <button type="button" class="opp-status" :class="`opp-status--${item.status}`" :disabled="!$store.getters.canWriteAiSuite" v-bind="attrs" v-on="on" @click.stop>
                                            <v-icon x-small class="opp-status__icon">{{ statusIcon(item.status) }}</v-icon>
                                            {{ statusLabel(item.status) }}
                                            <v-icon x-small>mdi-menu-down</v-icon>
                                        </button>
                                    </template>
                                    <v-list dense>
                                        <v-list-item v-for="opt in statusOptions" :key="`st-${opt.value}`" @click="setStatus(item, opt.value)">
                                            <v-list-item-icon class="mr-2"><v-icon small>{{ opt.icon }}</v-icon></v-list-item-icon>
                                            <v-list-item-title>{{ opt.text }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-icon v-if="item.note" x-small color="grey" class="ml-1" :title="item.note">mdi-note-text-outline</v-icon>
                            </template>
                            <template v-slot:item.score="{ item }">
                                <v-tooltip top max-width="340">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="opp-score" v-bind="attrs" v-on="on">
                                            <span class="opp-score__value" :class="scoreTone(item.score)">{{ item.score }}</span>
                                            <div class="opp-meter">
                                                <div class="opp-meter__fill" :class="scoreTone(item.score)" :style="{ width: item.score + '%' }"></div>
                                            </div>
                                        </div>
                                    </template>
                                    <span>{{ scoreExplanation(item) }}</span>
                                </v-tooltip>
                            </template>

                            <template v-slot:expanded-item="{ headers, item }">
                                <td :colspan="headers.length" class="opp-expand">
                                    <div class="opp-action" v-if="item.action">
                                        <span class="opp-action__icon"><v-icon small color="primary">{{ actionIcon(item.action) }}</v-icon></span>
                                        <div class="opp-action__body">
                                            <div class="opp-action__title">{{ actionTitle(item.action) }}</div>
                                            <div class="opp-action__text">{{ actionText(item) }}</div>
                                        </div>
                                        <div class="opp-action__btns">
                                            <v-btn
                                                small
                                                rounded
                                                outlined
                                                color="primary"
                                                class="opp-action__btn"
                                                :href="auditHref(item)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Opens the Page Audit with this prompt as focus">
                                                <v-icon small left>mdi-file-search-outline</v-icon>
                                                Audit a page for this prompt
                                            </v-btn>
                                            <v-menu v-if="contentSuiteEnabled" offset-y left max-width="460">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn small rounded outlined color="primary" class="opp-action__btn" v-bind="attrs" v-on="on">
                                                        <v-icon small left>mdi-file-document-edit-outline</v-icon>
                                                        Create content project
                                                    </v-btn>
                                                </template>
                                                <v-list dense>
                                                    <v-subheader>Start with this main keyword</v-subheader>
                                                    <v-list-item v-for="opt in contentKeywordOptions(item)" :key="`ck-${item.prompt}-${opt.keyword}`" @click="createContentProject(item, opt.keyword)">
                                                        <v-list-item-content>
                                                            <v-list-item-title>{{ opt.keyword }}</v-list-item-title>
                                                            <v-list-item-subtitle>{{ opt.source }}</v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </div>
                                    </div>
                                    <div class="opp-expand__grid">
                                        <div class="opp-expand__col">
                                            <div class="opp-expand__label">Named instead of you</div>
                                            <div v-if="detailOf(item).loading" class="opp-muted">Loading…</div>
                                            <div v-else-if="detailOf(item).brands.length">
                                                <div v-for="b in detailOf(item).brands" :key="`eb-${item.prompt}-${b.name}`" class="opp-expand__row">
                                                    <span class="opp-expand__name">{{ b.name }} <v-icon v-if="b.isCompetitor" x-small color="primary" title="in your competitor set">mdi-star</v-icon></span>
                                                    <span class="opp-expand__num">{{ b.count }}</span>
                                                </div>
                                            </div>
                                            <div v-else class="opp-muted">No brand named.</div>
                                        </div>
                                        <div class="opp-expand__col opp-expand__col--wide">
                                            <div class="opp-expand__label">Pages cited in those answers</div>
                                            <div v-if="detailOf(item).loading" class="opp-muted">Loading…</div>
                                            <div v-else-if="detailOf(item).urls.length">
                                                <div v-for="u in detailOf(item).urls" :key="`eu-${item.prompt}-${u.url}`" class="opp-expand__row">
                                                    <span class="opp-expand__name opp-expand__name--query">
                                                        <a v-if="safeHref(u.url)" :href="safeHref(u.url)" target="_blank" rel="noopener noreferrer" class="opp-link" :title="u.url">{{ shortUrl(u.url) }}</a>
                                                        <span v-else :title="u.url">{{ shortUrl(u.url) }}</span>
                                                        <span v-if="u.isOwn" class="opp-pill opp-pill--own">yours</span>
                                                        <a v-if="u.isOwn" :href="auditHref(item, u.url)" target="_blank" rel="noopener noreferrer" class="opp-link opp-link--audit">audit this page</a>
                                                    </span>
                                                    <span class="opp-expand__num">{{ u.count }}</span>
                                                </div>
                                            </div>
                                            <div v-else-if="detailOf(item).domains.length">
                                                <div v-for="d in detailOf(item).domains" :key="`ed-${item.prompt}-${d.name}`" class="opp-expand__row">
                                                    <span class="opp-expand__name">{{ d.name }}</span>
                                                    <span class="opp-expand__num">{{ d.count }}</span>
                                                </div>
                                            </div>
                                            <div v-else class="opp-muted">No sources.</div>
                                        </div>
                                        <div class="opp-expand__col">
                                            <div class="opp-expand__label">Page types cited</div>
                                            <div v-if="detailOf(item).loading" class="opp-muted">Loading…</div>
                                            <div v-else-if="detailOf(item).pageTypes.length">
                                                <div v-for="pt in detailOf(item).pageTypes" :key="`ep-${item.prompt}-${pt.name}`" class="opp-expand__row">
                                                    <span class="opp-expand__name">{{ pageTypeLabel(pt.name) }}</span>
                                                    <span class="opp-expand__num">{{ pt.count }}</span>
                                                </div>
                                            </div>
                                            <div v-else class="opp-muted">Not classified.</div>
                                        </div>
                                        <div class="opp-expand__col">
                                            <div class="opp-expand__label">What the models searched for</div>
                                            <div v-if="detailOf(item).loading" class="opp-muted">Loading…</div>
                                            <div v-else-if="detailOf(item).queries.length">
                                                <div v-for="q in detailOf(item).queries.slice(0, 6)" :key="`eq-${item.prompt}-${q.name}`" class="opp-expand__row">
                                                    <span class="opp-expand__name opp-expand__name--query" :title="q.name">{{ q.name }}</span>
                                                    <span class="opp-expand__num">{{ q.count }}</span>
                                                </div>
                                            </div>
                                            <div v-else class="opp-muted">No search queries recorded.</div>
                                        </div>
                                        <div class="opp-expand__col">
                                            <div class="opp-expand__label">Coverage</div>
                                            <div class="opp-expand__row">
                                                <span class="opp-expand__name">Platforms that name you</span>
                                                <span class="opp-expand__num">{{ item.ownPlatforms.length ? item.ownPlatforms.map(platformLabel).join(", ") : "none" }}</span>
                                            </div>
                                            <div class="opp-expand__row">
                                                <span class="opp-expand__name">Latest answers from</span>
                                                <span class="opp-expand__num">{{ formatDate(item.lastDate) }}</span>
                                            </div>
                                            <div class="opp-expand__row">
                                                <span class="opp-expand__name">Last time you were named</span>
                                                <span class="opp-expand__num">{{ item.lastDateWithOwn ? formatDate(item.lastDateWithOwn) : "never" }}</span>
                                            </div>
                                            <div class="opp-expand__row" v-if="detailOf(item).error"><span class="error--text">{{ detailOf(item).error }}</span></div>
                                        </div>
                                    </div>
                                    <div class="opp-noteedit">
                                        <div class="opp-expand__label">Note</div>
                                        <div class="opp-noteedit__row">
                                            <v-textarea v-model="noteDrafts[item.prompt]" :placeholder="item.note || 'What was done, who owns it, what to check next…'" :readonly="!$store.getters.canWriteAiSuite" rows="1" auto-grow dense outlined hide-details class="opp-noteedit__field" @focus="initNoteDraft(item)"></v-textarea>
                                            <v-btn small rounded color="primary" :disabled="!noteChanged(item) || !$store.getters.canWriteAiSuite" :loading="savingStatus === item.prompt" @click="saveNote(item)">Save</v-btn>
                                        </div>
                                    </div>
                                </td>
                            </template>

                            <template v-slot:no-data>
                                <div class="opp-empty">
                                    <span v-if="loading">Loading…</span>
                                    <span v-else-if="error">{{ error }}</span>
                                    <template v-else>
                                        <v-icon color="grey lighten-1">mdi-check-circle-outline</v-icon>
                                        <div>No gaps for the current filters. {{ ownBrandLabel }} is present in every tracked answer.</div>
                                    </template>
                                </div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- ======== WEAK MENTIONS ======== -->
                    <div v-if="view === 'weak'" class="opp-section">
                        <div class="opp-section__subtitle">
                            Prompts where {{ ownBrandLabel }} is named but comes off badly: mentioned after three or more other brands, or with negative sentiment.
                            <span v-if="weakMentions.projectRankAvg !== null"> Project average: position {{ weakMentions.projectRankAvg }}, sentiment {{ formatSentiment(weakMentions.projectSentimentAvg) }}.</span>
                        </div>
                        <v-data-table :headers="weakHeaders" :items="weakMentions.rows" :items-per-page="25" item-key="prompt" class="opp-table">
                            <template v-slot:item.prompt="{ item }">
                                <div class="opp-prompt">{{ item.prompt }}</div>
                                <div class="opp-prompt__tags" v-if="item.tags && item.tags.length">
                                    <span v-for="t in visibleTags(item.tags)" :key="`wt-${item.prompt}-${t}`" class="opp-tag opp-tag--link" @click.stop="openTopic(t)">{{ t }}</span>
                                </div>
                            </template>
                            <template v-slot:item.reasons="{ item }">
                                <div class="opp-tags">
                                    <span v-for="r in item.reasons" :key="`wr-${item.prompt}-${r}`" class="opp-pill" :class="`opp-pill--weak-${r}`">{{ weakReasonLabel(r) }}</span>
                                </div>
                            </template>
                            <template v-slot:item.ownResponses="{ item }">
                                <div class="opp-num">{{ item.ownResponses }} <span class="opp-num__of">/ {{ item.responses }}</span></div>
                                <div class="opp-num__sub">answers name you</div>
                            </template>
                            <template v-slot:item.rankAvg="{ item }">
                                <div class="opp-num">{{ item.mention.rankAvg !== null ? "#" + item.mention.rankAvg : "–" }}</div>
                                <div class="opp-meter opp-meter--sm" v-if="item.mention.lateShare !== null">
                                    <div class="opp-meter__fill opp-meter__fill--gap" :style="{ width: Math.round(item.mention.lateShare * 100) + '%' }"></div>
                                </div>
                                <div class="opp-num__sub" v-if="item.mention.lateShare !== null">{{ Math.round(item.mention.lateShare * 100) }}% after 3+ brands</div>
                            </template>
                            <template v-slot:item.sentimentAvg="{ item }">
                                <div class="opp-num" :class="sentimentTone(item.mention.sentimentAvg)">{{ formatSentiment(item.mention.sentimentAvg) }}</div>
                                <div class="opp-num__sub" v-if="item.mention.negativeShare !== null">{{ Math.round(item.mention.negativeShare * 100) }}% negative</div>
                            </template>
                            <template v-slot:item.weakScore="{ item }">
                                <div class="opp-score">
                                    <span class="opp-score__value" :class="scoreTone(item.weakScore)">{{ item.weakScore }}</span>
                                    <div class="opp-meter"><div class="opp-meter__fill" :class="scoreTone(item.weakScore)" :style="{ width: item.weakScore + '%' }"></div></div>
                                </div>
                            </template>
                            <template v-slot:no-data>
                                <div class="opp-empty">
                                    <v-icon color="grey lighten-1">mdi-check-circle-outline</v-icon>
                                    <div>No weak mentions in this range. Where {{ ownBrandLabel }} is named, it is named early and without negative sentiment.</div>
                                </div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- ======== COMPETITORS ======== -->
                    <div v-if="view === 'competitors'" class="opp-section">
                        <div class="opp-section__subtitle">Where each competitor is named in answers that do not mention {{ ownBrandLabel }}. Click a row to see those prompts.</div>
                        <v-data-table :headers="competitorHeaders" :items="competitorGaps" :items-per-page="15" item-key="name" class="opp-table opp-table--clickable" @click:row="(row) => toggleCompetitor(row.name)">
                            <template v-slot:item.name="{ item }">
                                <span class="opp-prompt opp-prompt--strong">{{ item.name }}</span>
                            </template>
                            <template v-slot:item.promptsWon="{ item }">
                                <div class="opp-num">{{ item.promptsWon }}</div>
                                <div class="opp-meter opp-meter--sm">
                                    <div class="opp-meter__fill" :style="{ width: sharePct(item.promptsWon, gapPromptBase) + '%' }"></div>
                                </div>
                                <div class="opp-num__sub">{{ sharePct(item.promptsWon, gapPromptBase) }}% of your gap prompts</div>
                            </template>
                            <template v-slot:item.answers="{ item }">
                                <div class="opp-num">{{ formatNum(item.answers) }}</div>
                            </template>
                            <template v-slot:item.topTags="{ item }">
                                <div class="opp-tags">
                                    <span v-for="t in item.topTags" :key="`ct-${item.name}-${t.name}`" class="opp-tag opp-tag--link" @click.stop="openTopic(t.name)">{{ t.name }} <em>{{ t.count }}</em></span>
                                </div>
                            </template>
                            <template v-slot:item.topPrompts="{ item }">
                                <div v-for="p in item.topPrompts.slice(0, 2)" :key="`cp-${item.name}-${p.prompt}`" class="opp-mini-prompt" :title="p.prompt">{{ p.prompt }}</div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- ======== CITATIONS ======== -->
                    <div v-if="view === 'citations'" class="opp-section">
                        <div class="opp-section__subtitle">Domains the models cite in answers that do not mention {{ ownBrandLabel }}. These are the pages to earn a mention on.</div>
                        <div class="opp-note" v-if="ownDomainCitations">
                            <v-icon small color="primary" class="mr-2">mdi-web</v-icon>
                            <span
                                >Your own domain was cited <b>{{ formatNum(ownDomainCitations) }}</b> times in answers that do not name {{ ownBrandLabel }}. The models trust those pages but do not recommend the brand
                                from them, so check how they present you.</span
                            >
                        </div>
                        <div class="opp-own-urls" v-if="ownDomainCitations && ownCitedUrls.length">
                            <button type="button" class="opp-linkbtn" @click="ownUrlsOpen = !ownUrlsOpen">
                                <v-icon x-small>{{ ownUrlsOpen ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                                {{ ownUrlsOpen ? "Hide" : "Show" }} your {{ ownCitedUrls.length }} most cited pages in those answers
                            </button>
                            <div v-if="ownUrlsOpen" class="opp-query-list mt-2">
                                <div v-for="u in ownCitedUrls" :key="`own-${u.url}`" class="opp-query">
                                    <span class="opp-query__rank"><v-icon x-small color="primary">mdi-web</v-icon></span>
                                    <span class="opp-query__text">
                                        <a v-if="safeHref(u.url)" :href="safeHref(u.url)" target="_blank" rel="noopener noreferrer" class="opp-link" :title="u.url">{{ shortUrl(u.url) }}</a>
                                        <span v-else>{{ shortUrl(u.url) }}</span>
                                        <span v-if="u.pageType" class="opp-tag ml-2">{{ pageTypeLabel(u.pageType) }}</span>
                                    </span>
                                    <span class="opp-query__count">{{ formatNum(u.count) }}×</span>
                                </div>
                            </div>
                        </div>
                        <div class="opp-seg opp-seg--small">
                            <button type="button" class="opp-seg__btn" :class="{ 'opp-seg__btn--active': citationMode === 'domains' }" @click="citationMode = 'domains'">Domains</button>
                            <button type="button" class="opp-seg__btn" :class="{ 'opp-seg__btn--active': citationMode === 'pages' }" @click="citationMode = 'pages'">Pages <span class="opp-seg__count">{{ citationUrls.length }}</span></button>
                        </div>
                        <v-data-table v-if="citationMode === 'pages'" :headers="citationUrlHeaders" :items="citationUrls" :items-per-page="25" item-key="url" class="opp-table">
                            <template v-slot:item.url="{ item }">
                                <a v-if="safeHref(item.url)" :href="safeHref(item.url)" target="_blank" rel="noopener noreferrer" class="opp-link opp-link--block" :title="item.url">{{ shortUrl(item.url) }}</a>
                                <span v-else class="opp-prompt">{{ shortUrl(item.url) }}</span>
                            </template>
                            <template v-slot:item.pageType="{ item }">
                                <span v-if="item.pageType" class="opp-tag">{{ pageTypeLabel(item.pageType) }}</span>
                            </template>
                            <template v-slot:item.count="{ item }">
                                <div class="opp-num">{{ formatNum(item.count) }}</div>
                                <div class="opp-meter opp-meter--sm">
                                    <div class="opp-meter__fill" :style="{ width: sharePct(item.count, citationUrls.length ? citationUrls[0].count : 0) + '%' }"></div>
                                </div>
                            </template>
                        </v-data-table>
                        <v-data-table v-else :headers="citationHeaders" :items="citationGaps" :items-per-page="25" item-key="domain" class="opp-table">
                            <template v-slot:item.domain="{ item }">
                                <span class="opp-prompt opp-prompt--strong">{{ item.domain }}</span>
                                <div v-if="item.topUrls && item.topUrls.length" class="opp-domain-urls">
                                    <div v-for="u in item.topUrls.slice(0, 3)" :key="`du-${item.domain}-${u.url}`" class="opp-domain-url">
                                        <a v-if="safeHref(u.url)" :href="safeHref(u.url)" target="_blank" rel="noopener noreferrer" class="opp-link" :title="u.url">{{ shortPath(u.url) }}</a>
                                        <span v-else>{{ shortPath(u.url) }}</span>
                                        <em>{{ formatNum(u.count) }}</em>
                                    </div>
                                </div>
                            </template>
                            <template v-slot:item.count="{ item }">
                                <div class="opp-num">{{ formatNum(item.count) }}</div>
                                <div class="opp-meter opp-meter--sm">
                                    <div class="opp-meter__fill" :style="{ width: sharePct(item.count, kpis && kpis.responsesWithGap) + '%' }"></div>
                                </div>
                                <div class="opp-num__sub">{{ sharePct(item.count, kpis && kpis.responsesWithGap) }}% of answers without you</div>
                            </template>
                            <template v-slot:item.pageTypes="{ item }">
                                <div class="opp-tags">
                                    <span v-for="pt in item.pageTypes" :key="`cpt-${item.domain}-${pt.name}`" class="opp-tag">{{ pageTypeLabel(pt.name) }} <em>{{ pt.count }}</em></span>
                                </div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- ======== SEARCH QUERIES ======== -->
                    <div v-if="view === 'queries'" class="opp-section">
                        <div class="opp-section__subtitle">What the models searched for while answering prompts that do not mention {{ ownBrandLabel }} ({{ formatNum(queriesTotal) }} queries). Terms first, then the queries that recur verbatim.</div>
                        <div class="opp-keywords">
                            <v-chip v-for="(k, idx) in keywords" :key="`kw-${k.term}`" small class="opp-kw" :class="keywordTier(idx)" color="teal lighten-5" text-color="teal darken-2">
                                {{ k.term }}
                                <span class="opp-kw__count">{{ k.queryCount }}</span>
                            </v-chip>
                            <div v-if="!keywords.length" class="opp-empty">
                                <v-icon color="grey lighten-1">mdi-magnify-close</v-icon>
                                <div>No search queries recorded for these answers.</div>
                            </div>
                        </div>
                        <div v-if="queries.length" class="opp-query-list">
                            <div class="opp-expand__label mb-2">Recurring queries</div>
                            <div v-for="(row, idx) in queries" :key="`q-${idx}`" class="opp-query" :title="row.query">
                                <span class="opp-query__rank">{{ idx + 1 }}</span>
                                <span class="opp-query__text">{{ row.query }}</span>
                                <span class="opp-query__count">{{ row.count }}×</span>
                            </div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </template>
    </div>
</template>

<script>
    const PAGE_TYPE_LABELS = {
        blog_guide: "Blog Guide",
        product_service_page: "Product / Service Page",
        category_hub: "Category Hub",
        homepage: "Homepage",
        comparison_page: "Comparison Page",
        recommendation_list: "Recommendation List",
        use_case_editorial: "Use Case Editorial",
        directory_profile: "Directory Profile",
        review_platform: "Review Platform",
        docs_help_api: "Docs / Help / API",
        company_trust_page: "Company Trust Page",
        news_press_editorial: "News / Press Editorial",
        community_ugc: "Community UGC",
        legal_utility: "Legal Utility",
        unknown_other: "Unknown / Other",
        video: "Video",
        video_content: "Video",
        article: "Article",
        news: "News",
        review: "Review",
        comparison: "Comparison",
        faq: "FAQ",
        docs: "Documentation",
        forum: "Forum",
        unknown: "Unknown"
    }

    // Lueckentypen in Alltagssprache. "explain" ist eine Funktion, weil der Markenname hinein muss.
    const GAP_TYPE_META = {
        never: { label: "Never named", short: "never named", explain: (brand) => `${brand} does not appear in any answer to this prompt.` },
        lost: { label: "Dropped out", short: "dropped out", explain: (brand) => `${brand} was named earlier in this period, but is missing from the latest answers.` },
        platform_gap: { label: "Missing on some platforms", short: "on some platforms", explain: (brand) => `${brand} is named on some AI platforms, but never on others.` },
        partial: { label: "Sometimes missing", short: "sometimes missing", explain: (brand) => `${brand} is named in some answers and left out in others, on the same platform.` }
    }

    const MINOR_GAP_EXPLAIN = "Missing from fewer than half of the answers and still named in the latest ones. Usually normal fluctuation."


    const ACTION_META = {
        create_content: { icon: "mdi-file-plus-outline", title: "Create content", text: "Not a single answer names you. Build a page that answers this prompt directly, so the models can cite it like the sources listed here." },
        check_changes: { icon: "mdi-history", title: "Check what changed", text: "You were named earlier in this period but not in the latest answers. Check what changed on your pages and which sources the models cite now." },
        platform_specific: { icon: "mdi-swap-horizontal", title: "Target the missing platforms", text: "Named on some AI platforms, never on others. Each platform relies on different sources, so look at what the missing ones cite." },
        strengthen: { icon: "mdi-arrow-up-bold-outline", title: "Strengthen the signal", text: "Named in some answers but not reliably. Strengthen the pages the models already cite so you show up every time." }
    }

    // Anzeigenamen der festen Plattform-Schluessel. getPlatformInfo liefert in dieser
    // Komponente keinen Label, in der Tabelle standen sonst die Rohwerte.
    const PLATFORM_LABELS = {
        chatgpt: "ChatGPT",
        openai: "ChatGPT",
        gemini: "Gemini",
        google: "AI Overviews",
        google_ai_overview: "AI Overviews",
        google_ai_mode: "AI Mode",
        perplexity: "Perplexity",
        microsoft_copilot: "Copilot",
        copilot: "Copilot",
        amazon_rufus: "Amazon Rufus",
        grok: "Grok"
    }

    const STATUS_META = {
        open: { label: "Open", icon: "mdi-circle-outline" },
        in_progress: { label: "In progress", icon: "mdi-progress-clock" },
        done: { label: "Done", icon: "mdi-check-circle-outline" },
        ignored: { label: "Ignored", icon: "mdi-eye-off-outline" }
    }

    // Tags, die auf fast jedem Prompt stehen, sagen in der Tabelle nichts aus
    const HIDDEN_TAGS = new Set(["unbranded-prompt", "branded-prompt", "unbranded", "branded"])

    export default {
        name: "AIProjectOpportunitiesView",
        props: {
            projectId: { type: [String, Number], default: "" },
            project: { type: Object, default: null },
            selectedDates: { type: Array, default: () => [] },
            selectedPlatform: { type: [Object, String], default: null },
            tags: { type: Array, default: () => [] },
            tagsOperator: { type: String, default: "or" },
            search: { type: String, default: "" },
            // Rohe Picker-Daten: selectedDates ist auf exakte processed_dates gesnappt und
            // taugt nicht als echter Zeitraum. Die Auswertung laeuft ueber den ganzen Range.
            dateRangeRaw: { type: Array, default: () => [] }
        },
        data() {
            return {
                loading: false,
                exporting: false,
                error: "",
                preparing: false,
                progress: { total: 0, done: 0 },
                pollTimer: null,
                view: "topics",
                explainerOpen: true,
                includeWeak: false,
                rows: [],
                total: 0,
                kpis: null,
                kpisPrev: null,
                trend: { hasPrevious: false, newGaps: 0, resolved: 0, persisting: 0, previousRange: null },
                resolvedRows: [],
                statusCounts: null,
                statusFilter: "active",
                trendFilter: null,
                noteDrafts: {},
                savingStatus: null,
                citationUrls: [],
                ownCitedUrls: [],
                citationMode: "domains",
                ownUrlsOpen: false,
                topicMode: "cards",
                weakMentions: { rows: [], total: 0, projectSentimentAvg: null, projectRankAvg: null },
                topicPlatform: { platforms: [], rows: [] },
                scopedByGapType: null,
                scopedTotal: null,
                topics: [],
                competitorGaps: [],
                citationGaps: [],
                ownDomainCitations: 0,
                queries: [],
                keywords: [],
                queriesTotal: 0,
                competitors: [],
                competitorSource: "top",
                gapTypeOrder: ["never", "lost", "platform_gap", "partial"],
                gapTypeFilter: "all",
                topicFilter: null,
                competitorFilter: null,
                gapSearch: "",
                gapSearchDebounce: null,
                fetchTimer: null,
                gapExpanded: [],
                gapDetails: {},
                reqSeq: 0,
                gapOptions: { page: 1, itemsPerPage: 25, sortBy: ["score"], sortDesc: [true] }
            }
        },
        computed: {
            ownBrandLabel() {
                return this.project?.brand?.brand_name || "your brand"
            },
            preparingPercent() {
                return this.progress.total ? Math.round((this.progress.done / this.progress.total) * 100) : 0
            },
            competitorSourceLabel() {
                return this.competitorSource === "favorites" ? "Competitor set · your favorites" : "Competitor set · top by mentions"
            },
            weakSwitchLabel() {
                const n = this.kpis ? this.kpis.promptsWithWeakGap : null
                return n ? `Also show ${n} minor gaps` : "Also show minor gaps"
            },
            minorGapExplain() {
                return MINOR_GAP_EXPLAIN
            },
            gapPromptBase() {
                if (!this.kpis) return 0
                return this.includeWeak ? this.kpis.promptsWithGap : this.kpis.promptsWithStrongGap
            },
            viewTabs() {
                return [
                    { key: "topics", label: "Topics", icon: "mdi-view-grid-outline", count: this.topics.length || null },
                    { key: "changes", label: "Changes", icon: "mdi-swap-vertical", count: this.trend.hasPrevious ? this.trend.newGaps + this.trend.resolved : null },
                    { key: "prompts", label: "Prompts", icon: "mdi-format-list-bulleted", count: this.total },
                    { key: "weak", label: "Weak mentions", icon: "mdi-thumb-down-outline", count: this.weakMentions.total || null },
                    { key: "competitors", label: "Competitors", icon: "mdi-account-group-outline", count: this.competitorGaps.length || null },
                    { key: "citations", label: "Citations", icon: "mdi-link-variant", count: this.citationGaps.length || null },
                    { key: "queries", label: "Search queries", icon: "mdi-magnify", count: null }
                ]
            },
            contentSuiteEnabled() {
                return Boolean(this.$store && this.$store.state && this.$store.state.content_suite)
            },
            weakHeaders() {
                return [
                    { text: "Prompt", value: "prompt", align: "start", sortable: false },
                    { text: "Why", value: "reasons", align: "start", sortable: false, width: 170 },
                    { text: "Mentions", value: "ownResponses", align: "start", width: 130 },
                    { text: "Avg position", value: "rankAvg", align: "start", sortable: false, width: 150 },
                    { text: "Sentiment", value: "sentimentAvg", align: "start", sortable: false, width: 120 },
                    { text: "Severity", value: "weakScore", align: "start", width: 120 }
                ]
            },
            previousRangeLabel() {
                const r = this.trend && this.trend.previousRange
                if (!r) return "n/a"
                return `${this.formatDate(r.from)} to ${this.formatDate(r.to)}`
            },
            statusOptions() {
                return Object.keys(STATUS_META).map((value) => ({ value, text: STATUS_META[value].label, icon: STATUS_META[value].icon }))
            },
            statusFilterOptions() {
                const c = this.statusCounts || {}
                return [
                    { text: "Active", value: "active", count: c.active ?? null },
                    { text: "In progress", value: "in_progress", count: c.in_progress ?? null },
                    { text: "Done", value: "done", count: c.done ?? null },
                    { text: "Ignored", value: "ignored", count: c.ignored ?? null },
                    { text: "All", value: "all", count: null }
                ]
            },
            citationUrlHeaders() {
                return [
                    { text: "Page", value: "url", align: "start" },
                    { text: "Domain", value: "domain", align: "start", width: 200 },
                    { text: "Page type", value: "pageType", align: "start", width: 200 },
                    { text: "Citations", value: "count", align: "start", width: 160 }
                ]
            },
            gapTypeOptions() {
                // Zaehler im aktuellen Kontext (Thema, Wettbewerber, schwache Luecken, Suche),
                // nicht die globalen KPIs — sonst steht bei 36 Prompts eines Themas "Lost 630".
                const byType = this.scopedByGapType || this.kpis?.byGapType || {}
                return [
                    { text: "All", value: "all", count: this.scopedTotal !== null ? this.scopedTotal : this.kpis ? this.gapPromptBase : null },
                    ...this.gapTypeOrder.map((type) => ({ text: this.gapTypeLabel(type), value: type, count: byType[type] ?? null }))
                ]
            },
            kpiCards() {
                const k = this.kpis
                const kp = this.kpisPrev
                const brand = this.ownBrandLabel
                const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0)
                // Delta zum Vorzeitraum. Bei allen vier Kennzahlen ist WENIGER besser.
                const delta = (now, before, unit = "") => {
                    if (!k || !kp || before === null || before === undefined) return null
                    const diff = now - before
                    if (!diff) return null
                    return { up: diff > 0, good: diff < 0, text: `${this.formatNum(Math.abs(diff))}${unit}` }
                }
                const d = {
                    strong: k && kp ? delta(k.promptsWithStrongGap, kp.promptsWithStrongGap) : null,
                    answers: k && kp ? delta(pct(k.responsesWithGap, k.responsesTotal), pct(kp.responsesWithGap, kp.responsesTotal), "pp") : null,
                    never: k && kp ? delta(k.byGapType?.never || 0, kp.byGapType?.never || 0) : null,
                    lost: k && kp ? delta(k.byGapType?.lost || 0, kp.byGapType?.lost || 0) : null
                }
                return [
                    {
                        key: "strong",
                        delta: d.strong,
                        tone: "primary",
                        icon: "mdi-target",
                        iconColor: "primary",
                        barColor: "primary",
                        label: "Prompts with a major gap",
                        value: k ? this.formatNum(k.promptsWithStrongGap) : "–",
                        total: k ? this.formatNum(k.promptsTotal) : null,
                        share: k ? pct(k.promptsWithStrongGap, k.promptsTotal) : null,
                        tooltip: `Prompts where ${brand} is never named, dropped out of the latest answers, or is missing from at least half of the answers in this period.`
                    },
                    {
                        key: "answers",
                        delta: d.answers,
                        tone: "primary",
                        icon: "mdi-comment-remove-outline",
                        iconColor: "primary",
                        barColor: "primary",
                        label: "Answers without you",
                        value: k ? this.formatNum(k.responsesWithGap) : "–",
                        total: k ? this.formatNum(k.responsesTotal) : null,
                        share: k ? pct(k.responsesWithGap, k.responsesTotal) : null,
                        tooltip: `Single AI answers in this period that do not mention ${brand}. One prompt produces one answer per platform and day.`
                    },
                    {
                        key: "never",
                        delta: d.never,
                        tone: "never",
                        icon: "mdi-close-circle-outline",
                        iconColor: "red darken-1",
                        label: "Never named",
                        value: k ? this.formatNum(k.byGapType?.never || 0) : "–",
                        share: null,
                        hint: "prompts without you in a single answer",
                        tooltip: `Prompts where ${brand} does not appear in any answer in this period, on any platform.`
                    },
                    {
                        key: "lost",
                        delta: d.lost,
                        tone: "lost",
                        icon: "mdi-trending-down",
                        iconColor: "amber darken-3",
                        label: "Dropped out",
                        value: k ? this.formatNum(k.byGapType?.lost || 0) : "–",
                        share: null,
                        hint: "named earlier, missing from the latest answers",
                        tooltip: `Prompts where ${brand} was named earlier in this period but is missing from the latest answers on every platform.`
                    }
                ]
            },
            gapHeaders() {
                return [
                    { text: "Prompt", value: "prompt", align: "start", sortable: true },
                    { text: "Gap type", value: "gapType", align: "start", sortable: false, width: 150 },
                    { text: "Answers without you", value: "gapResponses", align: "start", sortable: true, width: 150 },
                    { text: "Missing on", value: "gapPlatforms", align: "start", sortable: false },
                    { text: "Named instead of you", value: "competitors", align: "start", sortable: false },
                    { text: "Status", value: "status", align: "start", sortable: false, width: 140 },
                    { text: "Score", value: "score", align: "start", sortable: true, width: 120 },
                    { text: "", value: "data-table-expand" }
                ]
            },
            competitorHeaders() {
                return [
                    { text: "Competitor", value: "name", align: "start" },
                    { text: "Prompts where they are named, you are not", value: "promptsWon", align: "start", width: 200 },
                    { text: "Answers", value: "answers", align: "end", width: 100 },
                    { text: "Strongest topics", value: "topTags", align: "start", sortable: false },
                    { text: "Example prompts", value: "topPrompts", align: "start", sortable: false }
                ]
            },
            citationHeaders() {
                return [
                    { text: "Domain", value: "domain", align: "start" },
                    { text: "Citations", value: "count", align: "start", width: 180 },
                    { text: "Page types", value: "pageTypes", align: "start", sortable: false }
                ]
            },
            dateRange() {
                const dates = [...(this.dateRangeRaw || [])].filter(Boolean).sort()
                if (!dates.length) return null
                return { from: dates[0], to: dates[dates.length - 1] }
            },
            // Die globale Suche gehoert bewusst NICHT dazu: sie ist in diesem Tab ausgeblendet.
            inputSignature() {
                return JSON.stringify({
                    projectId: this.projectId,
                    range: this.dateRange,
                    platform: this.selectedPlatform?.key || this.selectedPlatform,
                    tags: this.tags,
                    tagsOperator: this.tagsOperator
                })
            }
        },
        watch: {
            inputSignature: {
                immediate: true,
                handler() {
                    this.gapOptions.page = 1
                    this.gapDetails = {}
                    this.gapExpanded = []
                    this.scheduleFetch()
                }
            },
            includeWeak() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            gapTypeFilter() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            topicFilter() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            competitorFilter() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            statusFilter() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            trendFilter() {
                this.gapOptions.page = 1
                this.scheduleFetch()
            },
            gapOptions: {
                deep: true,
                handler() {
                    this.scheduleFetch()
                }
            },
            gapExpanded(rows) {
                ;(Array.isArray(rows) ? rows : []).forEach((row) => this.fetchDetail(row && row.prompt))
            }
        },
        methods: {
            resolveApiRoot() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                    this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                ) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            formatNum(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return value == null ? "–" : String(value)
                return n.toLocaleString("de-DE")
            },
            // Kein globaler Mixin in diesem Projekt — ohne die Methode bricht die Tabelle beim
            // Rendern der Aufklapp-Zeile mit "formatDate is not a function" ab.
            formatDate(value) {
                if (!value) return "—"
                const dt = new Date(value)
                if (isNaN(dt)) return String(value)
                return dt.toISOString().slice(0, 10)
            },
            // Zitierte URLs stammen aus Modell-Antworten, also aus fremder Hand: nur http(s) wird
            // verlinkt, alles andere (javascript:, data:) bleibt reiner Text.
            safeHref(url) {
                return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null
            },
            shortUrl(url) {
                return String(url || "")
                    .replace(/^https?:\/\//i, "")
                    .replace(/^www\./i, "")
                    .replace(/\/$/, "")
            },
            shortPath(url) {
                const short = this.shortUrl(url)
                const idx = short.indexOf("/")
                return idx === -1 ? "/" : short.slice(idx)
            },
            formatSentiment(value) {
                if (value === null || value === undefined || !Number.isFinite(Number(value))) return "–"
                const n = Number(value)
                return (n > 0 ? "+" : "") + n.toFixed(2)
            },
            sentimentTone(value) {
                if (value === null || value === undefined) return ""
                if (value < 0) return "tone-neg"
                if (value < 0.1) return "tone-neutral"
                return "tone-pos"
            },
            weakReasonLabel(reason) {
                return { late: "Named late", negative: "Negative", below_average: "Below your average" }[reason] || reason
            },
            // Praesenz 0..1 als Farbe: rot (fehlt) ueber neutral nach teal (praesent)
            heatStyle(share) {
                const p = Math.max(0, Math.min(1, Number(share) || 0))
                if (p < 0.5) return { background: `rgba(239, 68, 68, ${(0.08 + (0.5 - p) * 0.72).toFixed(2)})`, color: p < 0.2 ? "#7f1d1d" : "inherit" }
                return { background: `rgba(0, 160, 137, ${(0.08 + (p - 0.5) * 0.9).toFixed(2)})`, color: p > 0.85 ? "#fff" : "inherit" }
            },
            // Vorschlaege fuer das Hauptkeyword eines Content-Projekts: was die Modelle zu diesem
            // Prompt tatsaechlich gesucht haben, dann die Themen-Tags, zuletzt der Prompt selbst.
            contentKeywordOptions(item) {
                const out = []
                const seen = new Set()
                const push = (keyword, source) => {
                    const k = String(keyword || "")
                        .replace(/\s+/g, " ")
                        .trim()
                        .slice(0, 120)
                    if (!k || seen.has(k.toLowerCase())) return
                    seen.add(k.toLowerCase())
                    out.push({ keyword: k, source })
                }
                ;(this.detailOf(item).queries || []).slice(0, 3).forEach((q) => push(q.name, "Search query the models used"))
                this.visibleTags(item.tags)
                    .filter((t) => !/^(informational|commercial|transactional|navigational)$/i.test(t) && t.length > 4)
                    .forEach((t) => push(t, "Topic tag"))
                push(item.prompt, "The prompt itself")
                return out
            },
            // deep link into the Page Audit: the prompt becomes the focus entry, an own cited page the URL
            // (see applyRouteQuery in SeoAudits.vue). Opens in a new tab so the worklist here stays as it is.
            auditHref(item, url) {
                const projectId = this.projectId || this.$route?.params?.id
                const query = { focus: String(item.prompt || "").slice(0, 200) }
                if (projectId) query.project = String(projectId)
                if (url) query.urls = String(url)
                return this.$router.resolve({ path: "/ai-suite/audit", query }).href
            },
            createContentProject(item, keyword) {
                const route = this.$router.resolve({ path: "/content-project/setup", query: { keyword, title: String(item.prompt || "").slice(0, 120) } })
                window.open(route.href, "_blank", "noopener")
            },
            statusLabel(status) {
                return STATUS_META[status]?.label || "Open"
            },
            statusIcon(status) {
                return STATUS_META[status]?.icon || "mdi-circle-outline"
            },
            showNewGaps() {
                this.statusFilter = "active"
                this.trendFilter = "new"
                this.view = "prompts"
            },
            initNoteDraft(item) {
                if (this.noteDrafts[item.prompt] === undefined) this.$set(this.noteDrafts, item.prompt, item.note || "")
            },
            noteChanged(item) {
                const draft = this.noteDrafts[item.prompt]
                return draft !== undefined && draft !== (item.note || "")
            },
            async saveNote(item) {
                await this.persistStatus(item, item.status || "open", this.noteDrafts[item.prompt] || "")
            },
            async setStatus(item, status) {
                const note = this.noteDrafts[item.prompt] !== undefined ? this.noteDrafts[item.prompt] : item.note || ""
                await this.persistStatus(item, status, note)
            },
            async persistStatus(item, status, note) {
                // AI-Suite-Rolle "nur lesen": Status und Notiz gelten für das ganze Projekt
                if (!this.$store.getters.canWriteAiSuite) return
                const projectId = this.projectId || this.$route?.params?.id
                if (!projectId) return
                this.savingStatus = item.prompt
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/opportunities/status/set", {
                        company_id: localStorage.company_id,
                        project_id: projectId,
                        prompt: item.prompt,
                        status,
                        note
                    })
                    // Sofort in der Zeile zeigen, dann neu laden: Zaehler und Filter haengen am Status.
                    this.$set(item, "status", resp?.data?.status || status)
                    this.$set(item, "note", resp?.data?.note ?? note)
                    this.$set(this.noteDrafts, item.prompt, item.note)
                    this.scheduleFetch()
                } catch (e) {
                    this.error = e?.response?.data?.error || "Could not save the status."
                } finally {
                    this.savingStatus = null
                }
            },
            sharePct(part, base) {
                const b = Number(base)
                if (!b) return 0
                return Math.min(100, Math.round((Number(part || 0) / b) * 100))
            },
            scoreTone(score) {
                if (score >= 75) return "tone-high"
                if (score >= 50) return "tone-mid"
                return "tone-low"
            },
            keywordTier(idx) {
                if (idx < 5) return "opp-kw--lg"
                if (idx < 15) return "opp-kw--md"
                return "opp-kw--sm"
            },
            topicPills(topic) {
                const byType = (topic && topic.byGapType) || {}
                return this.gapTypeOrder.filter((type) => byType[type]).map((type) => ({ type, count: byType[type] }))
            },
            visibleTags(tags) {
                return (tags || []).filter((t) => !HIDDEN_TAGS.has(String(t).toLowerCase())).slice(0, 3)
            },
            platformLabel(key) {
                const k = String(key || "").toLowerCase()
                if (PLATFORM_LABELS[k]) return PLATFORM_LABELS[k]
                const info = this.getPlatformInfo ? this.getPlatformInfo(key) : null
                return info?.label || key || "—"
            },
            pageTypeLabel(key) {
                const normalized = (key || "unknown").toString().trim().toLowerCase()
                if (PAGE_TYPE_LABELS[normalized]) return PAGE_TYPE_LABELS[normalized]
                return normalized.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
            },
            gapTypeLabel(type) {
                return GAP_TYPE_META[type]?.label || type
            },
            gapTypeShort(type) {
                return GAP_TYPE_META[type]?.short || type
            },
            gapTypeExplain(type) {
                const meta = GAP_TYPE_META[type]
                return meta && meta.explain ? meta.explain(this.ownBrandLabel) : ""
            },
            toggleExplainer() {
                this.explainerOpen = !this.explainerOpen
                try {
                    localStorage.setItem("opp_explainer_open", this.explainerOpen ? "1" : "0")
                } catch (e) {
                    // privater Modus o. ae. — dann merkt sich der Browser den Zustand eben nicht
                }
            },
            actionIcon(key) {
                return ACTION_META[key]?.icon || "mdi-lightbulb-outline"
            },
            actionTitle(key) {
                return ACTION_META[key]?.title || ""
            },
            actionText(item) {
                return ACTION_META[item.action]?.text || ""
            },
            // Score-Tooltip in Alltagssprache: erst was der Score bedeutet, dann die vier Anteile.
            scoreExplanation(item) {
                const p = item.scoreParts || {}
                const pct = (v) => `${Math.round((v || 0) * 100)}%`
                const parts = [
                    `${pct(p.gapShare)} of answers without you (counts 40%)`,
                    `competitors named instead: ${pct(p.pressure)} pressure (30%)`,
                    `${p.recency === 1 ? "missing from the latest answers" : "still in the latest answers"} (20%)`,
                    `${pct(p.breadth)} of platforms affected (10%)`
                ]
                return `Score ${item.score} of 100 = how worth working on this prompt is. ${parts.join(" · ")}`
            },
            openTopic(tag) {
                this.topicFilter = tag
                this.view = "prompts"
            },
            toggleCompetitor(name) {
                this.competitorFilter = this.competitorFilter === name ? null : name
                if (this.competitorFilter) this.view = "prompts"
            },
            onGapSearchInput() {
                if (this.gapSearchDebounce) clearTimeout(this.gapSearchDebounce)
                this.gapSearchDebounce = setTimeout(() => {
                    this.gapOptions.page = 1
                    this.scheduleFetch()
                }, 400)
            },
            // Mehrere Watcher feuern im selben Tick (Seite zurueck + Signatur) — zusammenfassen.
            scheduleFetch() {
                if (this.fetchTimer) clearTimeout(this.fetchTimer)
                this.fetchTimer = setTimeout(() => this.fetchAll(), 50)
            },
            basePayload() {
                const projectId = this.projectId || this.$route?.params?.id
                const payload = {
                    company_id: localStorage.company_id,
                    project_id: projectId,
                    importDateRange: { from: this.dateRange.from, to: this.dateRange.to }
                }
                const platformKey = this.selectedPlatform?.key || this.selectedPlatform
                if (platformKey && platformKey !== "all") payload.platform = platformKey
                if (this.tags && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagsOperator
                }
                return payload
            },
            listPayload(overrides = {}) {
                const sortField = this.gapOptions.sortBy?.[0] || "score"
                const sortDir = this.gapOptions.sortDesc?.[0] === false ? 1 : -1
                const payload = {
                    ...this.basePayload(),
                    skip: (this.gapOptions.page - 1) * this.gapOptions.itemsPerPage,
                    limit: this.gapOptions.itemsPerPage,
                    sort: { [sortField]: sortDir },
                    includeWeak: this.includeWeak
                }
                if (this.statusFilter && this.statusFilter !== "active") payload.status = this.statusFilter
                if (this.trendFilter) payload.trend = this.trendFilter
                if (this.gapTypeFilter && this.gapTypeFilter !== "all") payload.gapType = this.gapTypeFilter
                if (this.topicFilter) payload.topic = this.topicFilter
                if (this.competitorFilter) payload.competitor = this.competitorFilter
                if (this.gapSearch) payload.promptSearch = this.gapSearch
                return { ...payload, ...overrides }
            },
            async fetchAll() {
                const projectId = this.projectId || this.$route?.params?.id
                if (!projectId || !this.dateRange) return
                const seq = ++this.reqSeq
                this.loading = true
                this.error = ""
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/opportunities/prompt-gaps", this.listPayload())
                    if (seq !== this.reqSeq) return
                    const d = resp?.data || {}
                    if (d.preparing) {
                        this.preparing = true
                        this.progress = d.progress || { total: 0, done: 0 }
                        this.schedulePoll()
                        return
                    }
                    this.preparing = false
                    this.rows = Array.isArray(d.rows) ? d.rows : []
                    this.total = Number(d.total || 0)
                    this.kpis = d.kpis || null
                    this.kpisPrev = d.kpisPrev || null
                    this.trend = d.trend && typeof d.trend === "object" ? { hasPrevious: false, newGaps: 0, resolved: 0, persisting: 0, previousRange: null, ...d.trend } : { hasPrevious: false, newGaps: 0, resolved: 0, persisting: 0, previousRange: null }
                    this.resolvedRows = Array.isArray(d.resolvedRows) ? d.resolvedRows : []
                    this.statusCounts = d.statusCounts || null
                    this.weakMentions = d.weakMentions && Array.isArray(d.weakMentions.rows) ? d.weakMentions : { rows: [], total: 0, projectSentimentAvg: null, projectRankAvg: null }
                    this.topicPlatform = d.topicPlatform && Array.isArray(d.topicPlatform.rows) ? d.topicPlatform : { platforms: [], rows: [] }
                    this.citationUrls = Array.isArray(d.citationUrls) ? d.citationUrls : []
                    this.ownCitedUrls = Array.isArray(d.ownCitedUrls) ? d.ownCitedUrls : []
                    this.scopedByGapType = d.scopedByGapType || null
                    this.scopedTotal = typeof d.scopedTotal === "number" ? d.scopedTotal : null
                    this.topics = Array.isArray(d.topics) ? d.topics : []
                    this.competitorGaps = Array.isArray(d.competitorGaps) ? d.competitorGaps : []
                    this.citationGaps = Array.isArray(d.citationGaps) ? d.citationGaps : []
                    this.ownDomainCitations = Number(d.ownDomainCitations || 0)
                    this.queries = Array.isArray(d.queries) ? d.queries : []
                    this.keywords = Array.isArray(d.keywords) ? d.keywords : []
                    this.queriesTotal = Number(d.queriesTotal || 0)
                    this.competitors = Array.isArray(d.competitors) ? d.competitors : []
                    this.competitorSource = d.competitorSource || "top"
                } catch (e) {
                    if (seq !== this.reqSeq) return
                    this.rows = []
                    this.total = 0
                    this.kpis = null
                    this.error = e?.response?.data?.error || "Could not load opportunities."
                } finally {
                    if (seq === this.reqSeq) this.loading = false
                }
            },
            schedulePoll() {
                if (this.pollTimer) clearTimeout(this.pollTimer)
                this.pollTimer = setTimeout(() => this.fetchAll(), 3000)
            },
            detailOf(item) {
                const key = item && item.prompt
                return this.gapDetails[key] || { loading: true, error: "", brands: [], domains: [], urls: [], pageTypes: [], queries: [] }
            },
            async fetchDetail(prompt) {
                if (!prompt || this.gapDetails[prompt]) return
                if (!this.dateRange) return
                this.$set(this.gapDetails, prompt, { loading: true, error: "", brands: [], domains: [], urls: [], pageTypes: [], queries: [] })
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/opportunities/prompt-gaps/detail", { ...this.basePayload(), prompt })
                    const d = resp?.data || {}
                    this.$set(this.gapDetails, prompt, {
                        loading: false,
                        error: "",
                        brands: Array.isArray(d.brands) ? d.brands : [],
                        domains: Array.isArray(d.domains) ? d.domains : [],
                        urls: Array.isArray(d.urls) ? d.urls : [],
                        pageTypes: Array.isArray(d.pageTypes) ? d.pageTypes : [],
                        queries: Array.isArray(d.queries) ? d.queries : []
                    })
                } catch (e) {
                    this.$set(this.gapDetails, prompt, { loading: false, error: e?.response?.data?.error || "Could not load details.", brands: [], domains: [], urls: [], pageTypes: [], queries: [] })
                }
            },
            csvEscape(value) {
                if (value === null || value === undefined) return ""
                const str = String(value)
                if (/[",\n;]/.test(str)) return `"${str.replace(/"/g, '""')}"`
                return str
            },
            async exportRows() {
                if (!this.dateRange) return
                this.exporting = true
                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/opportunities/prompt-gaps", this.listPayload({ skip: 0, limit: 0 }))
                    const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : []
                    const header = ["Prompt", "Status", "Note", "Trend", "Gap type", "Signal", "Score", "Answers without brand", "Answers total", "Gap share", "Platforms with gap", "Named instead", "Domains cited", "Action", "Tags"]
                    const lines = [header.join(",")]
                    rows.forEach((row) => {
                        lines.push(
                            [
                                this.csvEscape(row.prompt),
                                this.csvEscape(this.statusLabel(row.status)),
                                this.csvEscape(row.note || ""),
                                this.csvEscape(row.trend || ""),
                                this.csvEscape(this.gapTypeLabel(row.gapType)),
                                this.csvEscape(row.signal),
                                this.csvEscape(row.score),
                                this.csvEscape(row.gapResponses),
                                this.csvEscape(row.responses),
                                this.csvEscape(`${(row.gapShare * 100).toFixed(1)}%`),
                                this.csvEscape((row.gapPlatforms || []).map((p) => this.platformLabel(p)).join(" | ")),
                                this.csvEscape((row.competitors || []).map((c) => `${c.name} (${c.count})`).join(" | ")),
                                this.csvEscape((row.domains || []).map((d) => `${d.name} (${d.count})`).join(" | ")),
                                this.csvEscape(ACTION_META[row.action]?.text || ""),
                                this.csvEscape((row.tags || []).join(" | "))
                            ].join(",")
                        )
                    })
                    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" })
                    const link = document.createElement("a")
                    link.href = URL.createObjectURL(blob)
                    link.download = `opportunities-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(link.href)
                } catch (e) {
                    this.error = e?.response?.data?.error || "Export failed."
                } finally {
                    this.exporting = false
                }
            }
        },
        created() {
            try {
                if (localStorage.getItem("opp_explainer_open") === "0") this.explainerOpen = false
            } catch (e) {
                // kein localStorage — Erklaerung bleibt offen
            }
        },
        beforeDestroy() {
            if (this.gapSearchDebounce) clearTimeout(this.gapSearchDebounce)
            if (this.fetchTimer) clearTimeout(this.fetchTimer)
            if (this.pollTimer) clearTimeout(this.pollTimer)
        }
    }
</script>

<style scoped>
    /* ---------- Farbtoken (hell) ---------- */
    .opportunities-view-wrap {
        --opp-ink: rgba(15, 23, 42, 0.9);
        --opp-ink-2: rgba(15, 23, 42, 0.7);
        --opp-muted: #64748b;
        --opp-faint: #b0b8c4;
        --opp-line: rgba(15, 23, 42, 0.08);
        --opp-surface: #f8fafc;
        --opp-surface-2: rgba(25, 118, 210, 0.04);
        --opp-teal-bg: rgba(0, 160, 137, 0.1);
        --opp-teal-ink: #00806d;
        --opp-red-bg: rgba(239, 68, 68, 0.1);
        --opp-red-ink: #b91c1c;
        --opp-amber-bg: rgba(245, 158, 11, 0.14);
        --opp-amber-ink: #b45309;
        --opp-blue-bg: rgba(14, 165, 233, 0.14);
        --opp-blue-ink: #0369a1;
        --opp-slate-bg: rgba(100, 116, 139, 0.14);
        --opp-slate-ink: #475569;
    }

    /* ---------- Vorbereitung ---------- */
    .opp-preparing {
        padding: 36px 24px;
        text-align: center;
        max-width: 640px;
        margin: 0 auto;
    }
    .opp-preparing__icon {
        display: inline-flex;
        width: 44px;
        height: 44px;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        background: var(--opp-teal-bg);
        margin-bottom: 12px;
    }
    .opp-preparing__title {
        font-size: 1rem;
        font-weight: 600;
        color: var(--opp-ink);
    }
    .opp-preparing__text {
        margin-top: 6px;
        font-size: 0.85rem;
        color: var(--opp-muted);
    }
    .opp-preparing__bar {
        margin-top: 18px;
    }

    /* ---------- KPI-Karten (wie Fanout-Tab) ---------- */
    .opp-kpi {
        border-top: 3px solid var(--v-primary-base);
    }
    .opp-kpi--never {
        border-top-color: #ef4444;
    }
    .opp-kpi--lost {
        border-top-color: #f59e0b;
    }
    .opp-kpi__head {
        display: flex;
        align-items: center;
        margin-bottom: 6px;
    }
    .opp-kpi__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: var(--opp-teal-bg);
        margin-right: 8px;
        flex: 0 0 24px;
    }
    .opp-kpi--never .opp-kpi__icon {
        background: var(--opp-red-bg);
    }
    .opp-kpi--lost .opp-kpi__icon {
        background: var(--opp-amber-bg);
    }
    .opp-kpi__label {
        display: inline-flex;
        align-items: center;
        font-size: 0.74rem;
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--opp-muted);
    }
    .opp-kpi__value {
        font-size: 1.7rem;
        font-weight: 600;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
    }
    .opp-kpi__total {
        font-size: 0.85rem;
        font-weight: 400;
        color: var(--opp-muted);
        margin-left: 4px;
    }
    .opp-kpi__bar-row {
        display: flex;
        align-items: center;
        margin-top: 8px;
        min-height: 16px;
    }
    .opp-kpi__bar {
        flex: 1 1 auto;
        margin-right: 8px;
    }
    .opp-kpi__share {
        font-size: 0.72rem;
        font-weight: 600;
        color: var(--v-primary-base);
        font-variant-numeric: tabular-nums;
    }
    .opp-kpi__hint {
        font-size: 0.72rem;
        color: var(--opp-faint);
    }

    /* ---------- Hauptkarte ---------- */
    .opp-card {
        overflow: hidden;
    }
    .opp-card__body {
        padding: 16px 16px 20px !important;
    }
    .opp-weak-switch {
        margin-top: 0;
        padding-top: 0;
    }
    .opp-weak-switch >>> .v-label {
        font-size: 0.82rem;
        color: var(--opp-ink-2);
    }
    .opp-context {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 16px;
        min-height: 28px;
        margin-bottom: 16px;
    }
    .opp-context__group {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
    }
    .opp-context__label {
        font-size: 0.82rem;
        font-weight: 700;
        color: var(--opp-ink);
        margin-right: 4px;
    }
    .opp-chip {
        cursor: pointer;
        font-weight: 500;
    }
    .opp-chip__count {
        margin-left: 6px;
        font-size: 0.72rem;
        font-weight: 700;
        opacity: 0.65;
        font-variant-numeric: tabular-nums;
    }

    /* Segment-Umschalter */
    .opp-seg {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 2px;
        padding: 3px;
        border-radius: 999px;
        background: var(--opp-surface);
        border: 1px solid var(--opp-line);
        margin-bottom: 16px;
    }
    .opp-seg-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px 16px;
        margin-bottom: 16px;
    }
    .opp-seg-row .opp-seg {
        margin-bottom: 0;
    }
    .opp-seg__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 14px;
        border-radius: 999px;
        border: 0;
        background: transparent;
        color: var(--opp-ink-2);
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
    }
    .opp-seg__btn:hover {
        color: var(--opp-ink);
    }
    .opp-seg__btn--active {
        background: #fff;
        color: var(--v-primary-base);
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12), 0 4px 10px -6px rgba(15, 23, 42, 0.25);
    }
    .opp-seg__icon {
        opacity: 0.8;
    }
    .opp-seg__count {
        font-size: 0.7rem;
        font-weight: 700;
        padding: 1px 7px;
        border-radius: 999px;
        background: var(--opp-slate-bg);
        color: var(--opp-slate-ink);
        font-variant-numeric: tabular-nums;
    }
    .opp-seg__btn--active .opp-seg__count {
        background: var(--opp-teal-bg);
        color: var(--opp-teal-ink);
    }

    .opp-section__subtitle {
        font-size: 0.8rem;
        color: var(--opp-muted);
        margin-bottom: 12px;
    }
    .opp-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 28px 12px;
        text-align: center;
        font-size: 0.85rem;
        color: var(--opp-muted);
    }
    .opp-muted {
        font-size: 0.78rem;
        color: var(--opp-muted);
    }
    .opp-note {
        display: flex;
        align-items: flex-start;
        padding: 10px 12px;
        margin-bottom: 12px;
        border-radius: 10px;
        background: var(--opp-teal-bg);
        font-size: 0.82rem;
        color: var(--opp-ink);
    }

    /* Kontextzeile + Erklaerung "What counts as a gap?" */
    .opp-explainer {
        margin-bottom: 14px;
    }
    .opp-explainer__line {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 14px;
        min-height: 24px;
        font-size: 0.82rem;
        color: var(--opp-muted);
    }
    .opp-explainer__hint b {
        color: var(--opp-ink);
        font-weight: 700;
    }
    .opp-explainer__toggle {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--opp-teal-ink);
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
    }
    .opp-explainer__toggle .v-icon {
        color: inherit !important;
    }
    .opp-explainer__toggle:hover .opp-explainer__title,
    .opp-explainer__toggle:focus-visible .opp-explainer__title {
        text-decoration: underline;
    }
    .opp-explainer__body {
        margin-top: 12px;
        padding: 14px 16px;
        border-radius: 12px;
        border: 1px solid var(--opp-line);
        background: var(--opp-surface);
    }
    .opp-explainer__text {
        margin: 0 0 12px;
        font-size: 0.82rem;
        line-height: 1.5;
        color: var(--opp-ink-2);
        max-width: 920px;
    }
    .opp-explainer__text--muted {
        margin-bottom: 0;
        color: var(--opp-muted);
    }
    .opp-explainer__types {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px 20px;
        margin-bottom: 14px;
    }
    .opp-explainer__type {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        font-size: 0.8rem;
        line-height: 1.4;
        color: var(--opp-ink-2);
    }
    .opp-explainer__type .opp-pill {
        flex: 0 0 auto;
        margin: 0;
    }

    /* Pills und Tags */
    .opp-pill {
        display: inline-block;
        padding: 2px 9px;
        border-radius: 999px;
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        white-space: nowrap;
        margin: 0 4px 3px 0;
    }
    .opp-pill--never {
        background: var(--opp-red-bg);
        color: var(--opp-red-ink);
    }
    .opp-pill--lost {
        background: var(--opp-amber-bg);
        color: var(--opp-amber-ink);
    }
    .opp-pill--platform_gap {
        background: var(--opp-blue-bg);
        color: var(--opp-blue-ink);
    }
    .opp-pill--partial {
        background: var(--opp-slate-bg);
        color: var(--opp-slate-ink);
    }
    .opp-tag {
        display: inline-block;
        padding: 1px 8px;
        margin: 0 4px 3px 0;
        border-radius: 999px;
        font-size: 0.72rem;
        font-weight: 500;
        background: var(--opp-surface);
        border: 1px solid var(--opp-line);
        color: var(--opp-ink-2);
        white-space: nowrap;
    }
    .opp-tag em {
        font-style: normal;
        font-weight: 700;
        opacity: 0.6;
        margin-left: 3px;
        font-variant-numeric: tabular-nums;
    }
    .opp-tag--teal {
        background: var(--opp-teal-bg);
        border-color: transparent;
        color: var(--opp-teal-ink);
    }
    .opp-tag--link {
        cursor: pointer;
    }
    .opp-tag--link:hover {
        border-color: var(--v-primary-base);
        color: var(--v-primary-base);
    }
    .opp-weak {
        font-size: 0.66rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--opp-faint);
        margin-top: 2px;
    }

    /* Zahlen mit Mini-Balken */
    .opp-num {
        font-weight: 700;
        font-size: 0.9rem;
        color: var(--opp-ink);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
    .opp-num__of {
        font-weight: 400;
        color: var(--opp-muted);
    }
    .opp-num__sub {
        font-size: 0.7rem;
        color: var(--opp-muted);
        white-space: nowrap;
        margin-top: 2px;
    }
    .opp-meter {
        height: 5px;
        border-radius: 3px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
        min-width: 70px;
    }
    .opp-meter--sm {
        height: 4px;
        margin-top: 4px;
        max-width: 110px;
    }
    .opp-meter__fill {
        height: 100%;
        border-radius: 3px;
        background: var(--v-primary-base);
    }
    .opp-meter__fill--gap {
        background: #f59e0b;
    }
    .opp-score {
        display: flex;
        flex-direction: column;
        gap: 4px;
        cursor: help;
        min-width: 70px;
    }
    .opp-score__value {
        font-weight: 800;
        font-size: 0.95rem;
        font-variant-numeric: tabular-nums;
        line-height: 1;
    }
    .opp-score__value.tone-high,
    .opp-meter__fill.tone-high {
        color: #b91c1c;
        background-color: transparent;
    }
    .opp-meter__fill.tone-high {
        background-color: #ef4444;
    }
    .opp-score__value.tone-mid {
        color: #b45309;
    }
    .opp-meter__fill.tone-mid {
        background-color: #f59e0b;
    }
    .opp-score__value.tone-low {
        color: var(--opp-slate-ink);
    }
    .opp-meter__fill.tone-low {
        background-color: #94a3b8;
    }

    /* ---------- Themenkarten ---------- */
    .opp-topic-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 16px;
    }
    /* Feste Rhythmik: 20 px Rand, 14 px zwischen Bloecken, 8 px innerhalb, 6 px zwischen Chips */
    .opp-topic {
        display: flex;
        flex-direction: column;
        gap: 14px;
        border: 1px solid var(--opp-line);
        border-radius: 14px;
        padding: 20px;
        background: #fff;
        cursor: pointer;
        transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
        outline: none;
    }
    .opp-topic:hover,
    .opp-topic:focus-visible {
        border-color: rgba(0, 160, 137, 0.45);
        box-shadow: 0 6px 18px -8px rgba(15, 23, 42, 0.25);
        transform: translateY(-1px);
    }
    .opp-topic__head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
    }
    .opp-topic__title {
        font-size: 1rem;
        font-weight: 700;
        color: var(--opp-ink);
        word-break: break-word;
        line-height: 1.3;
    }
    .opp-topic__score {
        display: inline-flex;
        align-items: baseline;
        gap: 5px;
        height: 24px;
        padding: 0 10px;
        border-radius: 999px;
        background: var(--opp-teal-bg);
        color: var(--opp-teal-ink);
        white-space: nowrap;
        line-height: 24px;
        flex: 0 0 auto;
    }
    .opp-topic__score-label {
        font-size: 0.64rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.8;
    }
    .opp-topic__score-value {
        font-size: 0.9rem;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
    }
    .opp-topic__presence-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0 8px;
        margin-bottom: 8px;
    }
    .opp-topic__presence-row .opp-delta {
        margin-left: 0;
    }
    .opp-topic__presence-pct {
        font-size: 1.5rem;
        font-weight: 800;
        line-height: 1.1;
        color: var(--opp-ink);
        font-variant-numeric: tabular-nums;
    }
    .opp-topic__presence-text {
        font-size: 0.76rem;
        color: var(--opp-muted);
    }
    .opp-topic__gaps {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-top: 14px;
        border-top: 1px solid var(--opp-line);
        font-size: 0.82rem;
        color: var(--opp-ink-2);
    }
    .opp-topic__gaps-count b {
        color: var(--opp-ink);
    }
    .opp-topic__pills {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .opp-topic__pills .opp-pill {
        margin: 0;
    }
    .opp-topic__foot {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: auto;
    }
    .opp-topic__row {
        display: grid;
        grid-template-columns: 96px minmax(0, 1fr);
        gap: 0 12px;
        align-items: start;
        font-size: 0.76rem;
    }
    .opp-topic__row-label {
        white-space: nowrap;
        font-size: 0.66rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        line-height: 24px;
        color: var(--opp-muted);
    }
    .opp-topic__row-value {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        min-width: 0;
    }
    .opp-topic__row-value .opp-tag {
        display: inline-flex;
        align-items: center;
        height: 24px;
        margin: 0;
        padding: 0 9px;
        font-size: 0.75rem;
    }

    /* ---------- Tabellen ---------- */
    .opp-filterbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px 16px;
        margin-bottom: 10px;
    }
    .opp-filterbar__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
    }
    .opp-search {
        max-width: 260px;
    }
    .opp-table >>> thead th {
        font-size: 0.72rem !important;
        font-weight: 600 !important;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--opp-muted) !important;
        white-space: nowrap;
    }
    .opp-table >>> tbody td {
        padding-top: 10px !important;
        padding-bottom: 10px !important;
        vertical-align: top;
    }
    .opp-table--clickable >>> tbody tr {
        cursor: pointer;
    }
    .opp-prompt {
        font-size: 0.86rem;
        line-height: 1.35;
        color: var(--opp-ink);
        min-width: 300px;
        max-width: 460px;
        word-break: break-word;
    }
    .opp-prompt--strong {
        min-width: 0;
    }
    .opp-prompt--strong {
        font-weight: 600;
    }
    .opp-prompt__tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 6px;
    }
    .opp-prompt__tags .opp-tag {
        margin: 0;
    }
    /* Tag-/Pill-Listen in Tabellenzellen: gleichmaessige Umbrueche per gap statt margin */
    .opp-tags {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
    }
    .opp-tags .opp-tag,
    .opp-tags .opp-pill {
        margin: 0;
    }
    .opp-mini-prompt {
        font-size: 0.76rem;
        color: var(--opp-ink-2);
        max-width: 360px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* Aufklapp-Zeile */
    .opp-expand {
        padding: 16px 20px 20px !important;
        background: var(--opp-surface) !important;
        box-shadow: inset 0 3px 8px -6px rgba(15, 23, 42, 0.25);
    }
    .opp-action {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        margin-bottom: 14px;
        border-radius: 10px;
        background: #fff;
        border: 1px solid rgba(0, 160, 137, 0.35);
        border-left-width: 4px;
    }
    .opp-action__icon {
        display: inline-flex;
        width: 28px;
        height: 28px;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background: var(--opp-teal-bg);
        flex: 0 0 28px;
    }
    .opp-action__title {
        font-size: 0.82rem;
        font-weight: 700;
        color: var(--opp-ink);
    }
    .opp-action__text {
        font-size: 0.78rem;
        color: var(--opp-ink-2);
        margin-top: 1px;
    }
    .opp-expand__grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
        gap: 14px 22px;
    }
    .opp-expand__label {
        font-size: 0.66rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--opp-muted);
        margin-bottom: 8px;
    }
    .opp-expand__row {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        font-size: 0.78rem;
        padding: 3px 0;
        border-bottom: 1px solid rgba(15, 23, 42, 0.05);
        color: var(--opp-ink);
    }
    .opp-expand__name {
        min-width: 0;
    }
    .opp-expand__name--query {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .opp-expand__num {
        font-weight: 700;
        color: var(--opp-ink-2);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    /* ---------- Suchbegriffe ---------- */
    .opp-keywords {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 16px;
    }
    .opp-keywords .opp-empty {
        flex: 1 1 100%;
    }
    .opp-kw {
        margin: 0;
    }
    .opp-kw--lg {
        font-size: 0.85rem;
        font-weight: 600;
    }
    .opp-kw--md {
        font-size: 0.78rem;
    }
    .opp-kw--sm {
        font-size: 0.72rem;
    }
    .opp-kw__count {
        opacity: 0.6;
        margin-left: 5px;
        font-variant-numeric: tabular-nums;
    }
    .opp-query-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        max-height: 420px;
        overflow-y: auto;
    }
    .opp-query {
        display: grid;
        grid-template-columns: 26px 1fr auto;
        gap: 10px;
        align-items: center;
        border: 1px solid var(--opp-line);
        border-radius: 10px;
        padding: 7px 10px;
        background: var(--opp-surface);
    }
    .opp-query__rank {
        font-size: 0.72rem;
        font-weight: 700;
        color: var(--opp-muted);
        font-variant-numeric: tabular-nums;
    }
    .opp-query__text {
        font-size: 0.82rem;
        color: var(--opp-ink);
        word-break: break-word;
    }
    .opp-query__count {
        font-size: 0.72rem;
        font-weight: 700;
        color: var(--opp-teal-ink);
        background: var(--opp-teal-bg);
        padding: 1px 8px;
        border-radius: 999px;
        white-space: nowrap;
    }

    /* ---------- Deltas, Aenderungen, Arbeitsliste, Links ---------- */
    .opp-delta {
        display: inline-flex;
        align-items: center;
        gap: 1px;
        margin-left: 8px;
        padding: 1px 7px;
        border-radius: 999px;
        font-size: 0.7rem;
        font-weight: 700;
        vertical-align: middle;
        font-variant-numeric: tabular-nums;
        cursor: default;
    }
    .opp-delta .v-icon {
        color: inherit !important;
    }
    .opp-delta--good {
        background: rgba(34, 197, 94, 0.12);
        color: #15803d;
    }
    .opp-delta--bad {
        background: var(--opp-red-bg);
        color: var(--opp-red-ink);
    }
    .opp-change-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
    }
    .opp-change {
        border: 1px solid var(--opp-line);
        border-left: 4px solid #94a3b8;
        border-radius: 14px;
        padding: 16px 20px;
        background: #fff;
    }
    .opp-change--bad {
        border-left-color: #ef4444;
        cursor: pointer;
    }
    .opp-change--bad:hover {
        box-shadow: 0 6px 18px -8px rgba(15, 23, 42, 0.25);
    }
    .opp-change--good {
        border-left-color: #22c55e;
    }
    .opp-change__value {
        font-size: 1.7rem;
        font-weight: 600;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
        color: var(--opp-ink);
    }
    .opp-change__label {
        font-size: 0.86rem;
        font-weight: 600;
        color: var(--opp-ink);
        margin-top: 4px;
    }
    .opp-change__hint {
        font-size: 0.74rem;
        color: var(--opp-muted);
        margin-top: 2px;
    }
    .opp-resolved {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 14px;
        border: 1px solid var(--opp-line);
        border-radius: 10px;
        padding: 7px 12px;
        background: var(--opp-surface);
    }
    .opp-resolved__prompt {
        font-size: 0.82rem;
        color: var(--opp-ink);
        min-width: 0;
        word-break: break-word;
    }
    .opp-resolved__shares {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
        font-size: 0.76rem;
        font-variant-numeric: tabular-nums;
    }
    .opp-resolved__from {
        color: var(--opp-muted);
        text-decoration: line-through;
    }
    .opp-resolved__to {
        font-weight: 700;
        color: #15803d;
    }
    .opp-filterbar--second {
        margin-bottom: 12px;
    }
    .opp-filterbar--second .opp-filterbar__chips {
        align-items: center;
    }
    .opp-pill--new {
        background: var(--v-primary-base);
        color: #fff;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
    .opp-pill--closed {
        background: rgba(34, 197, 94, 0.14);
        color: #15803d;
    }
    .opp-pill--own {
        background: var(--opp-teal-bg);
        color: var(--opp-teal-ink);
        margin-left: 6px;
    }
    .opp-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 6px 3px 9px;
        border-radius: 999px;
        border: 1px solid var(--opp-line);
        background: var(--opp-surface);
        color: var(--opp-ink-2);
        font-size: 0.74rem;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
    }
    .opp-status .v-icon {
        color: inherit !important;
    }
    .opp-status--in_progress {
        background: var(--opp-blue-bg);
        border-color: transparent;
        color: var(--opp-blue-ink);
    }
    .opp-status--done {
        background: rgba(34, 197, 94, 0.14);
        border-color: transparent;
        color: #15803d;
    }
    .opp-status--ignored {
        background: var(--opp-slate-bg);
        border-color: transparent;
        color: var(--opp-slate-ink);
    }
    .opp-noteedit {
        margin-top: 14px;
    }
    .opp-noteedit__row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
    }
    .opp-noteedit__field {
        max-width: 720px;
        background: #fff;
        font-size: 0.82rem;
    }
    .opp-expand__col--wide {
        grid-column: span 2;
        min-width: 0;
    }
    .opp-link {
        color: var(--v-primary-base);
        text-decoration: none;
    }
    .opp-link:hover {
        text-decoration: underline;
    }
    .opp-link--block {
        display: inline-block;
        max-width: 520px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.84rem;
        vertical-align: bottom;
    }
    .opp-linkbtn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border: 0;
        background: transparent;
        color: var(--v-primary-base);
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        padding: 0;
    }
    .opp-own-urls {
        margin-bottom: 14px;
    }
    .opp-seg--small {
        margin-bottom: 12px;
    }
    .opp-seg--small .opp-seg__btn {
        padding: 4px 12px;
        font-size: 0.78rem;
    }
    .opp-domain-urls {
        margin-top: 4px;
    }
    .opp-domain-url {
        font-size: 0.74rem;
        max-width: 420px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--opp-ink-2);
    }
    .opp-domain-url em {
        font-style: normal;
        font-weight: 700;
        opacity: 0.6;
        margin-left: 5px;
        font-variant-numeric: tabular-nums;
    }
    .theme--dark .opp-change,
    .theme--dark .opp-noteedit__field {
        background: rgba(255, 255, 255, 0.06);
    }
    .theme--dark .opp-delta--good,
    .theme--dark .opp-pill--closed,
    .theme--dark .opp-status--done,
    .theme--dark .opp-resolved__to {
        color: #86efac;
    }

    /* ---------- Heatmap, schwache Nennungen, Content-Bruecke ---------- */
    .opp-heat-wrap {
        overflow-x: auto;
    }
    .opp-heat {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 4px;
    }
    .opp-heat th {
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--opp-muted);
        text-align: center;
        padding: 4px 8px;
        white-space: nowrap;
    }
    .opp-heat td {
        text-align: center;
        padding: 2px 4px;
    }
    .opp-heat tbody tr {
        cursor: pointer;
    }
    .opp-heat tbody tr:hover .opp-heat__topic {
        color: var(--v-primary-base);
    }
    .opp-heat th.opp-heat__topic,
    .opp-heat td.opp-heat__topic {
        text-align: left;
        font-size: 0.86rem;
        font-weight: 600;
        color: var(--opp-ink);
        white-space: nowrap;
        padding-right: 16px;
    }
    .opp-heat__n {
        margin-left: 8px;
        font-size: 0.7rem;
        font-weight: 400;
        color: var(--opp-muted);
    }
    .opp-heat__cell {
        display: inline-block;
        min-width: 64px;
        padding: 6px 10px;
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--opp-ink);
    }
    .opp-heat__cell--empty {
        background: var(--opp-surface);
        color: var(--opp-faint);
        font-weight: 400;
    }
    .opp-heat__legend {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-top: 12px;
    }
    .opp-heat__legend .opp-heat__cell {
        min-width: 44px;
        padding: 3px 6px;
        font-size: 0.68rem;
    }
    .opp-pill--weak-late {
        background: var(--opp-amber-bg);
        color: var(--opp-amber-ink);
    }
    .opp-pill--weak-negative {
        background: var(--opp-red-bg);
        color: var(--opp-red-ink);
    }
    .opp-pill--weak-below_average {
        background: var(--opp-slate-bg);
        color: var(--opp-slate-ink);
    }
    .opp-num.tone-neg {
        color: var(--opp-red-ink);
    }
    .opp-num.tone-neutral {
        color: var(--opp-slate-ink);
    }
    .opp-num.tone-pos {
        color: #15803d;
    }
    .opp-action__body {
        flex: 1 1 auto;
        min-width: 0;
    }
    .opp-action__btns {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 8px;
        flex: 0 0 auto;
        align-self: center;
    }
    .opp-action__btn {
        flex: 0 0 auto;
        align-self: center;
        background: #fff;
    }
    .opp-link--audit {
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-left: 6px;
    }

    /* ---------- Dunkles Theme ---------- */
    .theme--dark .opportunities-view-wrap,
    .theme--dark.opportunities-view-wrap {
        --opp-ink: rgba(241, 245, 249, 0.94);
        --opp-ink-2: rgba(226, 232, 240, 0.8);
        --opp-muted: #a8b1bd;
        --opp-faint: #7c8794;
        --opp-line: rgba(255, 255, 255, 0.12);
        --opp-surface: rgba(255, 255, 255, 0.05);
        --opp-surface-2: rgba(66, 165, 245, 0.1);
        --opp-teal-bg: rgba(0, 160, 137, 0.2);
        --opp-teal-ink: #5eead4;
        --opp-red-bg: rgba(239, 68, 68, 0.22);
        --opp-red-ink: #fca5a5;
        --opp-amber-bg: rgba(245, 158, 11, 0.22);
        --opp-amber-ink: #fcd34d;
        --opp-blue-bg: rgba(14, 165, 233, 0.24);
        --opp-blue-ink: #7dd3fc;
        --opp-slate-bg: rgba(148, 163, 184, 0.2);
        --opp-slate-ink: #cbd5e1;
    }
    .theme--dark .opp-topic,
    .theme--dark .opp-action,
    .theme--dark .opp-seg__btn--active {
        background: rgba(255, 255, 255, 0.06);
    }
    .theme--dark .opp-seg__btn--active {
        color: var(--opp-teal-ink);
    }
    .theme--dark .opp-expand {
        background: rgba(255, 255, 255, 0.03) !important;
    }
    .theme--dark .opp-meter {
        background: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .opp-kpi__value {
        color: rgba(241, 245, 249, 0.94);
    }
</style>
